import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import { Tooltip } from 'components/Widget/Tooltip';
import SubmitButton, { SubmitButtonState } from 'components/Widget/Button/SubmitButton';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import { Loader } from 'components/Widget/Loader';
import * as api from 'dragonmouse-shared/api/forge';
import { DomElement, Frame, FrameStatus, PartialFrame } from 'dragonmouse-shared/interfaces';
import { CurrencyLevel } from 'economy';
import { useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectUser, selectWalletLevels } from 'redux/db/reducers';
import { setSelectedDomElements, showConfirmModal, setActiveSceneId } from 'redux/mode/actions';
import { executeCommand } from 'redux/mode/commandManager';
import { MemorializeFrameUpdateCommand } from 'redux/mode/commands';
import { displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import store from 'redux/store';
import { useTheme } from 'styled-components';
import { getElementName, navigate } from 'utils';
import { profile } from 'routes';
import { trackEconomyAction } from 'analytics';
import { toast } from 'react-toastify';
import * as S from './styles';

interface EditInputProps {
	frame: Frame;
	prototypeWidth?: number;
}

const EditInput = ({ frame, prototypeWidth = 0 }: EditInputProps) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const [prompt, setPrompt] = useState<string>('');
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const inputRef = useRef<HTMLTextAreaElement>(null);

	const { selectedDomElements, user, walletLevels } = useSelector(
		(r: RootReducer) => ({
			selectedDomElements: r.mode.selectedDomElements,
			user: selectUser(r.db),
			walletLevels: selectWalletLevels(r.db),
		}),
		shallowEqual
	);
	const numberOfEdits = user?.metadata.numberOfEdits || 0;
	const silverLevel: CurrencyLevel | undefined = walletLevels?.silverLevel;

	// Responsive suggestion logic
	const showResponsiveSuggestion = prototypeWidth && Math.abs(prototypeWidth - frame.width) > 100;

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------
	useEffect(() => {
		// Focus the input when elements are selected
		if (selectedDomElements.length > 0 && inputRef.current) {
			inputRef.current.focus();
		}
	}, [selectedDomElements]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const showSilverUpgradeModal = () => {
		trackEconomyAction('feature_fast_edit_upgrade_modal_shown');
		dispatch(
			showConfirmModal({
				title: "You're out of silver",
				description:
					'Upgrade to Pro or Max (or wait for silver to refill) to continue making fast edits.',
				cancelLabel: 'Close',
				submitLabel: 'Upgrade',
				onSubmit: () => {
					trackEconomyAction('fast_edit_upgrade_clicked');
					dispatch(setActiveSceneId(null));
					navigate(profile());
				},
			})
		);
	};

	const onSubmit = async (customPrompt?: string) => {
		if (!!frame?.viewOnly) {
			displayViewOnlyModal();
			return;
		}

		// Refresh wallet to ensure we have the latest balance before checking
		try {
			await forge.fetchWallet();
			// Get updated wallet levels after refresh
			const state = store.getState();
			const updatedWalletLevels = selectWalletLevels(state.db);
			if (updatedWalletLevels?.silverLevel === 'EMPTY') {
				showSilverUpgradeModal();
				return;
			}
		} catch (error) {
			console.error('Failed to refresh wallet before submission:', error);
			// Check cached wallet data if refresh failed
			if (silverLevel === 'EMPTY') {
				showSilverUpgradeModal();
				return;
			}
		}

		setIsSubmitting(true);

		let editResponse: api.EditResponse | undefined = undefined;
		try {
			// Update the existing frame's status
			await forge.updateFrame({
				update: {
					id: frame.id,
					metadata: {
						...frame.metadata,
						status: FrameStatus.PROCESSING,
						statusMessage: customPrompt ? 'Making responsive...' : 'initializing edit...',
					},
				},
			});

			const req: api.EditRequest = {
				sourceFrameId: frame.id,
				targetFrameId: frame.id,
				prompt: customPrompt || prompt,
				selectedDomElements: customPrompt ? [] : selectedDomElements,
			};

			editResponse = await forge.edit(req);
			forge.updateUser({
				update: {
					id: user?.id,
					metadata: {
						numberOfEdits: numberOfEdits + 1,
						lastEditDate: new Date().toISOString(),
					},
				},
			});
		} catch (e: any) {
			if (e.status === 400) {
				const errorMessage = e.response?.data?.error || e.message;
				// Check if error is about insufficient silver balance
				const isInsufficientSilver =
					errorMessage && errorMessage.toLowerCase().includes('insufficient silver');

				if (isInsufficientSilver) {
					showSilverUpgradeModal();
				} else {
					// Show toast for other errors (not insufficient silver)
					toast.error(errorMessage || 'Edit failed');
				}

				await forge.updateFrame({
					update: {
						id: frame.id,
						metadata: {
							...frame.metadata,
							status: FrameStatus.ERROR,
							statusMessage: errorMessage,
						},
					},
				});
			} else {
				// Show toast for non-400 errors
				const errorMessage =
					e.response?.data?.error || e.response?.data?.message || e.message || 'Edit failed';
				toast.error(errorMessage);
			}
		} finally {
			if (!customPrompt) {
				setPrompt('');
				dispatch(setSelectedDomElements([]));
			}
			setIsSubmitting(false);
		}

		if (editResponse?.newSiteId) {
			const before: PartialFrame = {
				id: frame.id,
				siteId: frame.siteId,
			};
			const after: PartialFrame = {
				id: frame.id,
				siteId: editResponse.newSiteId,
			};
			executeCommand(new MemorializeFrameUpdateCommand([before], [after]));
		}
	};

	const handleMakeResponsive = () => {
		const responsivePrompt = `make this responsive so it looks good, comfortably filling all screen widths`;
		onSubmit(responsivePrompt);
	};

	const onChangePrompt = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setPrompt(e.target.value);
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			onSubmit();
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderSilverIndicator = () => {
		const tooltipId = 'fast-edit-silver-indicator-tooltip';
		let tooltipContent = 'Fast edits require silver.';
		if (silverLevel === 'LOW') tooltipContent += '<br><br>⚠️ Silver is low.';
		else if (silverLevel === 'EMPTY') tooltipContent += '<br><br>⚠️ You are out of silver.';
		else tooltipContent += '<br><br>You have ample silver available.';

		// Wrap content in div with inline styles to ensure proper wrapping
		tooltipContent = `<div style="max-width: 240px; line-height: 1.6; word-wrap: break-word; white-space: normal; overflow-wrap: break-word;">${tooltipContent}</div>`;

		return (
			<S.SilverIndicatorWrapper data-tooltip-id={tooltipId} data-tooltip-html={tooltipContent}>
				<S.SilverCircle walletLevel={silverLevel}>
					{silverLevel === 'LOW' && (
						<S.ErrorIconOverlay>
							<Icon type={IconType.BANG} width={15} height={15} color={theme.gray[100]} />
						</S.ErrorIconOverlay>
					)}
				</S.SilverCircle>
				<Tooltip id={tooltipId} placement="top" />
			</S.SilverIndicatorWrapper>
		);
	};

	const renderActions = () => {
		const hasInput = prompt.trim().length > 0;
		return (
			<S.Actions>
				{renderSilverIndicator()}
				<S.Spacer />
				{isSubmitting ? (
					<S.LoadingWrapper>
						<Loader width={20} height={20} />
					</S.LoadingWrapper>
				) : (
					<S.SubmitButtonWrapper>
						<SubmitButton
							state={
								hasInput && !isSubmitting ? SubmitButtonState.REGULAR : SubmitButtonState.DISABLED
							}
							type={IconType.LINE_ARROW_UP}
							tooltip={'Submit edit'}
							tooltipPlacement="bottom-start"
							onClick={onSubmit}
						/>
					</S.SubmitButtonWrapper>
				)}
			</S.Actions>
		);
	};

	return (
		<S.Base>
			{showResponsiveSuggestion && (
				<S.ResponsiveSuggestion>
					<S.SuggestionText>
						Layout not looking good at this width?{' '}
						<S.SuggestionLink onClick={handleMakeResponsive} disabled={isSubmitting}>
							{isSubmitting ? 'Making responsive...' : 'make it responsive'}
						</S.SuggestionLink>
					</S.SuggestionText>
				</S.ResponsiveSuggestion>
			)}
			<S.InputArea isFocused={isFocused}>
				{selectedDomElements.length > 0 && (
					<S.SelectedElements>
						{selectedDomElements.map((element: DomElement) => (
							<S.SelectedElement key={element.id}>{getElementName(element)}</S.SelectedElement>
						))}
					</S.SelectedElements>
				)}
				<S.MultilineInputWrapper>
					<MultilineInput
						value={prompt}
						onInput={onChangePrompt}
						onKeyDown={onKeyDown}
						placeholder="What would you like to change?"
						minLines={2}
						renderBorder={false}
						onFocusChange={(isFocused) => {
							setIsFocused(isFocused);
						}}
						ref={inputRef}
					/>
				</S.MultilineInputWrapper>
				{renderActions()}
			</S.InputArea>
		</S.Base>
	);
};

export default EditInput;
